import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight } from "lucide-react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { REVIEW_STATUS, NEEDS_ATTENTION } from "../../lib/planReview";
import { Pill } from "../../components/onboarding/adminUi";
import { btn, fmtDate } from "../../components/leads/PriorityBadge";

const ORDER = ["info_review", "form_completed", "draft_ready", "coach_review", "info_needed", "draft_generating", "approved", "form_pending", "published", "none"];

const Item = ({ it, onOpen }) => (
  <button onClick={() => onOpen(it)} data-testid={`pr-item-${it.key}`}
    style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "15px 18px", background: "none", border: "none", borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#fff", textAlign: "left", cursor: "pointer" }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 700, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {it.player_name}
        <Pill map={REVIEW_STATUS} k={it.status || "none"} testid={`pr-status-${it.key}`} />
        <span style={{ fontSize: 11, color: "#737373" }}>{it.source === "client" ? "Cliente actual" : "Incorporación"}</span>
      </div>
      <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap" }}>
        {it.submitted_at && <span>Formulario enviado {fmtDate(it.submitted_at)}</span>}
        {it.alerts.map((a, i) => (
          <span key={i} style={{ color: a.level === "high" ? "#f87171" : a.level === "medium" ? "#facc15" : "#a3a3a3", display: "inline-flex", gap: 4, alignItems: "center" }}>
            {a.level !== "info" && <AlertTriangle size={12} />}{a.missing ? `Faltan ${a.missing.length} datos` : a.text}
          </span>
        ))}
      </div>
    </div>
    <ChevronRight size={18} style={{ color: "#737373", flexShrink: 0 }} />
  </button>
);

export default function AdminPlanReviews() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("attention");
  useEffect(() => { api.get("/admin/plan-reviews/board").then((r) => setItems(r.data)).catch((e) => { toast.error(formatErr(e)); setItems([]); }); }, []);

  const open = async (it) => {
    if (it.review_id) return navigate(`/app/admin/planificaciones/${it.review_id}`);
    if (it.source === "onboarding" && !it.user_id) return navigate(`/app/admin/incorporaciones/${it.onboarding_id}`);
    try {
      const { data } = await api.post("/admin/plan-reviews", { user_id: it.user_id, onboarding_id: it.onboarding_id });
      navigate(`/app/admin/planificaciones/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const counts = (items || []).reduce((a, i) => ({ ...a, [i.status || "none"]: (a[i.status || "none"] || 0) + 1 }), {});
  const attention = (items || []).filter((i) => NEEDS_ATTENTION.includes(i.status) || i.alerts.some((a) => a.level === "high"));
  const shown = (filter === "attention" ? attention : filter === "all" ? items || [] : (items || []).filter((i) => (i.status || "none") === filter))
    .slice().sort((a, b) => ORDER.indexOf(a.status || "none") - ORDER.indexOf(b.status || "none"));

  return (
    <AppShell>
      <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2rem", letterSpacing: "0.06em", margin: 0 }}>Planificaciones</h1>
      <p style={{ color: "#a3a3a3", fontSize: 13, margin: "4px 0 18px" }}>Revisa, edita y aprueba cada planificación antes de que la vea el futbolista.</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <button onClick={() => setFilter("attention")} data-testid="pr-filter-attention" style={{ ...btn(filter === "attention" ? "#dc2626" : "transparent"), borderRadius: 999 }}>Necesitan atención · {attention.length}</button>
        <button onClick={() => setFilter("all")} data-testid="pr-filter-all" style={{ ...btn(filter === "all" ? "#dc2626" : "transparent"), borderRadius: 999 }}>Todos · {(items || []).length}</button>
        {ORDER.filter((k) => counts[k]).map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`pr-filter-${k}`}
            style={{ ...btn(filter === k ? "#dc2626" : "transparent"), borderRadius: 999, color: filter === k ? "#fff" : REVIEW_STATUS[k].fg }}>
            {REVIEW_STATUS[k].label} · {counts[k]}
          </button>
        ))}
      </div>
      <div style={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 }} data-testid="pr-list">
        {items === null && <div style={{ padding: 24, textAlign: "center", color: "#a3a3a3" }}>Cargando…</div>}
        {items && shown.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#a3a3a3" }} data-testid="pr-empty">Nada por aquí.</div>}
        {shown.map((it) => <Item key={it.key} it={it} onOpen={open} />)}
      </div>
    </AppShell>
  );
}
